import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { ReactElement, useEffect, useState } from 'react';
import {
  AppPrimaryButton,
  ConfirmDialog,
} from 'shared/components/AppComponents';
import PageHeader from 'shared/components/PageHeader';
import DeleteIcon from '@mui/icons-material/Delete';
import { ReactComponent as Plus } from 'assets/images/ic_plus.svg';
import CreateApiKeyDialog from './CreateApiKeyDialog';
import { GuardrailsService } from 'services/GuardrailsService';
import { Utils } from 'shared/helpers';
import EmptyPlaceholder from 'shared/components/EmptyPlaceholder';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { FormikHelpers } from 'formik';
import moment from 'moment';
import { IServer } from '../servers/ServerList';
import { useSelector } from 'react-redux';
import { IAppState } from 'state/RootReducer';

export interface IApiKeyProps {
  _id: string;
  name: string;
  rateLimitRequestsPerMinute: string;
  expiresAt: string;
  allowedServers: any[];
  apiKey: string;
  serverUrl: string;
  status: string;
  isActive: boolean;
  createdBy: string;
  updatedBy: any;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

const APIKeyList = (): ReactElement => {
  const [openDeleteAPIkey, setOpenDeleteAPIkey] = useState(false);
  const [apiCreateOpenDialog, setApiCreateOpenDialog] = useState(false);
  const [apiKeys, setApiKeys] = useState<IApiKeyProps[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedKey, setSelectedKey] = useState('');
  const [servers, setServers] = useState<IServer>();
  const intialValues = {
    name: '',
    expiryDate: null,
    limit: '60',
    allowedServers: [],
  };
  const [apiKeyForm, setApiKeyForm] = useState({ ...intialValues });
  const { orgACLConfig } = useSelector(
    (state: IAppState) => state.baseAppState
  );

  const handleCloseDeleteServer = (): void => {
    setOpenDeleteAPIkey(false);
  };

  const getApiKeyList = (): void => {
    setIsLoading(true);
    GuardrailsService.getApiKeys()
      .then((res) => {
        const { apiKeys } = res.data.data;
        setApiKeys(apiKeys);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getServerLists = (): void => {
    GuardrailsService.getserverList()
      .then((res) => {
        const { servers } = res.data.data;
        setServers(servers);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      });
  };

  const actionOnStatus = (id: string, status: string): void => {
    GuardrailsService.updateApiKey(id, { status })
      .then(() => {
        getApiKeyList();
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      });
  };

  const handleDeleteServer = (): void => {
    GuardrailsService.deleteApiKey(selectedKey)
      .then((res) => {
        getApiKeyList();
        Utils.handleSuccessResponse(res);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      });
  };

  const actionOnSubmit = (
    formValues: any,
    formik: FormikHelpers<any>
  ): void => {
    const { name, expiryDate, limit, allowedServers } = formValues;
    const requestObj = {
      name,
      rateLimitRequestsPerMinute: limit,
      expiresAt: expiryDate,
      allowedServers,
    };
    GuardrailsService.createApiKey(requestObj)
      .then((res) => {
        getApiKeyList();
        Utils.handleSuccessResponse(res);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => {
        formik.resetForm();
        formik.setSubmitting(false);
        setSelectedKey('');
        setApiCreateOpenDialog(false);
      });
  };

  useEffect(() => {
    getApiKeyList();
    getServerLists();
  }, []);

  return (
    <>
      <PageHeader
        title="API Keys"
        description="Manage API keys for MCP clients to authenticate with the proxy."
        noPadding
        actions={
          <>
            {orgACLConfig.policies?.includes(
              'policies.guardrails.apikeys.create'
            ) && (
              <AppPrimaryButton
                className="btn-small"
                onClick={() => setApiCreateOpenDialog(true)}
                startIcon={
                  <SvgIcon
                    component={Plus}
                    inheritViewBox
                    className="view-icon"
                    fontSize="inherit"
                  />
                }
                title="Create API Key"
              />
            )}
          </>
        }
      />
      {isLoading && (
        <Box className="empty-content">
          <CircularProgress />
        </Box>
      )}
      {!isLoading && apiKeys.length > 0 && (
        <TableContainer>
          <Table
            className="app-table"
            sx={{ minWidth: 650 }}
            aria-label="simple table"
          >
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Key Prefix</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Rate Limit</TableCell>
                <TableCell>Last Used</TableCell>
                <TableCell>Expires</TableCell>
                {(orgACLConfig.policies?.includes(
                  'policies.guardrails.apikeys.delete'
                ) ||
                  orgACLConfig.policies?.includes(
                    'policies.guardrails.apikeys.activate'
                  )) && <TableCell>Actions</TableCell>}
              </TableRow>
            </TableHead>

            <TableBody>
              {apiKeys.map((key) => (
                <TableRow>
                  <TableCell>{key.name}</TableCell>
                  <TableCell>{key.apiKey}</TableCell>
                  <TableCell className="cursor-pointer">
                    {key.status === 'active' ? (
                      <Chip
                        label="Active"
                        sx={{ background: 'black', color: 'white' }}
                        size="small"
                      />
                    ) : (
                      <Chip
                        label="In-Active"
                        sx={{ background: 'gray', color: 'white' }}
                        size="small"
                      />
                    )}
                  </TableCell>
                  <TableCell>{key.rateLimitRequestsPerMinute} RPM</TableCell>
                  <TableCell>{key.updatedBy ? '-' : 'Never'}</TableCell>
                  <TableCell>
                    {moment(key.createdAt).format('DD/MM/YYYY')}
                  </TableCell>
                  <TableCell>
                    {orgACLConfig.policies?.includes(
                      'policies.guardrails.apikeys.delete'
                    ) && (
                      <IconButton
                        color="error"
                        onClick={() => {
                          setSelectedKey(key._id);
                          setOpenDeleteAPIkey(true);
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                    {orgACLConfig.policies?.includes(
                      'policies.guardrails.apikeys.activate'
                    ) && (
                      <AppPrimaryButton
                        title={
                          key.status === 'active' ? 'Deactivate' : 'Activate'
                        }
                        className="btn-extra-small"
                        onClick={() =>
                          actionOnStatus(
                            key._id,
                            key.status === 'active' ? 'inactive' : 'active'
                          )
                        }
                      />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {!isLoading && apiKeys.length === 0 && (
        <Box className="app-plain-card empty-content">
          <EmptyPlaceholder
            icon={<VpnKeyIcon fontSize="large" />}
            title="No API keys created yet. Create your first key for MCP client auth."
          />
        </Box>
      )}
      <ConfirmDialog
        title="Delete API Key"
        subTitle="This will permanently revoke this API key. Any MCP clients using it will lose access immediately."
        showConfirm={openDeleteAPIkey}
        cancelTitle="Cancel"
        successTitle="Delete"
        onSuccess={() => {
          handleCloseDeleteServer();
          handleDeleteServer();
        }}
        onCancel={handleCloseDeleteServer}
        onCloseDialog={handleCloseDeleteServer}
      />
      {apiCreateOpenDialog && (
        <CreateApiKeyDialog
          open={apiCreateOpenDialog}
          onCloseApiKey={() => setApiCreateOpenDialog(false)}
          apiKeyForm={apiKeyForm}
          setApiKeyForm={setApiKeyForm}
          actionOnSubmit={actionOnSubmit}
          servers={servers}
        />
      )}
    </>
  );
};

export default APIKeyList;
